import { Component } from '@theme/component';

/**
 * `<be-collection-link-component>` wraps the "All Wines" back-link pill on the
 * product page. When the shopper arrived from a collection page on this store,
 * the pill points back to that collection (filters, page and vintage grouping
 * included) instead of its server-rendered default, and a plain click uses
 * `history.back()` so the collection's saved scroll position is restored (see
 * scroll-container.js). Direct arrivals, external referrers and product →
 * product hops keep the default href.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} link - The pill anchor
 *
 * @extends {Component<Refs>}
 */
class BeCollectionLinkComponent extends Component {
  requiredRefs = ['link'];

  /** @type {string | null} */
  #collectionUrl = null;

  /** @type {number} */
  #historyLength = 0;

  /** @type {boolean} */
  #hashChanged = false;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    const collectionUrl = getReferringCollectionUrl();
    if (!collectionUrl) return;

    this.#collectionUrl = collectionUrl;
    this.#historyLength = history.length;
    this.refs.link.href = collectionUrl;

    // Hash-triggered overlays (#be-newsletter, #be-booking-overlay…) push an
    // entry on top of the product page, so `history.back()` would no longer
    // land on the collection.
    this.#abortController = new AbortController();
    window.addEventListener('hashchange', () => (this.#hashChanged = true), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Returns to the referring collection, preferring `history.back()` when the
   * previous history entry is still that collection.
   * @param {MouseEvent} event
   */
  navigate(event) {
    if (!this.#collectionUrl) return;

    // Modifier / non-primary clicks open the collection in a new tab as usual.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // No previous entry (opened in a new tab) or extra entries since load: let
    // the anchor navigate to the collection URL instead.
    if (history.length < 2 || history.length !== this.#historyLength || this.#hashChanged) return;

    event.preventDefault();
    history.back();
  }
}

/**
 * Returns the referrer URL when it is a collection page on this store.
 * @returns {string | null}
 */
function getReferringCollectionUrl() {
  if (!document.referrer) return null;

  try {
    const url = new URL(document.referrer);
    if (url.origin !== window.location.origin) return null;

    // `/collections/<handle>` with an optional locale prefix. Product pages
    // reached through a collection (`/collections/<handle>/products/<handle>`)
    // and the collection list (`/collections`) don't count.
    const segments = url.pathname.split('/').filter(Boolean);
    const index = segments.indexOf('collections');
    if (index === -1 || !segments[index + 1] || segments.includes('products')) return null;

    return url.href;
  } catch {
    return null;
  }
}

if (!customElements.get('be-collection-link-component')) {
  customElements.define('be-collection-link-component', BeCollectionLinkComponent);
}
